import {
  Box,
  Container,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  styled,
  Chip,
  IconButton,
  Grid,
  Drawer,
  useTheme,
  useMediaQuery,
  Divider,
  Button,
  GlobalStyles
} from '@mui/material'
import { PieChart } from '@mui/x-charts/PieChart'
import PageProps from '../_types/page-props.interface'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { StyleHelper } from '../components/style-helper'
import { useError } from '../context/error'
import { useGlobalState } from '../context/global'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import { HeatersService } from '../services/heaters.service'
import { SectorHeater, Stock } from '../_types/sector-heaters.type'
import { Formatters } from '../_helpers/formatter.helper'
import LoadingTable from '../components/loading/table.loading'
import KnowledgeBtn from '../components/controls/knowledge-btn'
import { ArrowOutwardRounded, Close, TrendingUpOutlined, Print, AccountBalanceWallet } from '@mui/icons-material'
import useBoolean from '../hooks/useBoolean'
import CarnivoreLogo from '../components/imgs/carnivore-logo'
import { StockService } from '../services/stock.service'
import { AlertService } from '../services/alert.service'
import { CarnivoreAlert } from '../_types/alert.type'
import { StockInfo } from '../_types/stock-info.type'
import StockPageLoadingState from '../components/loading/stock-page.loading'
import StockLogo from '../components/imgs/stock-logo'
import AlertView from '../components/alert-view'
import { useSearchParams } from 'react-router-dom'
import ViewAlertModal from '../components/modals/view-alert.modal'
import StockRow from '../components/stock-row'
import TickerLink from '../components/ticker-link'
import SectorPerformanceModal from '../components/modals/sector-performance.modal'

const CASH_AMOUNT = 76255.11

const PIE_COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF5722', '#3F51B5', '#8BC34A', '#FFC107',
  '#607D8B', '#795548', '#009688', '#CDDC39', '#F44336',
  '#673AB7', '#03A9F4', '#FF6F00', '#1B5E20', '#AD1457',
  '#0D47A1', '#E65100', '#4A148C', '#006064', '#BF360C',
  '#1A237E'
]

function TickerWrapper({ children }: { children: ReactElement }) {
  return (
    <TableCell component='th' scope='row'>
      {children}
    </TableCell>
  )
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0
  }
}))

const _calculatePercentageChange = (
  initialValue: number,
  finalValue: number
): string => {
  const change = finalValue - initialValue
  return Formatters.formatPercentage((change / initialValue) * 100)
}

export default function HeatersPage(props: PageProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  useDocumentTitle(props.title);
  const { setError } = useError();
  const { user } = useGlobalState();
  const [loading, setLoading] = useState(false);
  const heatersService = new HeatersService(setError);
  const [heaters, setHeaters] = useState<SectorHeater[]>([]);
  const [ticker, setSelectedTicker] = useState<string>(searchParams.has('ticker') ? searchParams.get('ticker')! : '');
  const [tickerDrawerIsOpen, { toggle, setTrue }] = useBoolean(searchParams.has('ticker'));
  const [performanceOpen, setPerformanceOpen] = useState(false)
  // State to handle sorting field and order for each heater
  const [sortConfig, setSortConfig] = useState<{ [key: string]: { field: keyof Stock; order: 'asc' | 'desc' } }>({});

  function handleTickerSelection(ticker: string) {
    setSearchParams({ ticker });
    setTrue();
    setSelectedTicker(ticker);
  }

  useEffect(() => {
    _getHeaters();
  }, []);

  const _getHeaters = async () => {
    setLoading(true);
    const res = await heatersService.getCurrentHeaters();
    setHeaters(res.heaters);
    setLoading(false);
  };

  const handleSort = (heaterId: string, field: any) => {
    setSortConfig((prev) => ({
      ...prev,
      [heaterId]: {
        field,
        order: prev[heaterId]?.order === 'asc' ? 'desc' : 'asc'
      }
    }));
  };

  const sortedStocks = (stocks: Stock[], heaterId: string) => {
    const { field, order } = sortConfig[heaterId] || {};
    if (!field) return stocks;

    return [...stocks].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return order === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return order === 'asc' ? aValue - bValue : bValue - aValue;
      }
      return 0;
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const portfolioData = useMemo(() => {
    const allStocks = heaters.flatMap(h => h.stocks);
    const rawSlices = allStocks
      .filter(s => s.shares && s.shares > 0 && s.currentPX)
      .map((s, i) => ({
        id: i,
        value: s.shares! * (s.currentPX ?? 0),
        ticker: s.symbol,
        color: PIE_COLORS[i % PIE_COLORS.length]
      }));

    const totalStockValue = rawSlices.reduce((sum, s) => sum + s.value, 0);
    const totalPortfolio = totalStockValue + CASH_AMOUNT;

    const stockSlices = rawSlices.map(s => ({
      ...s,
      label: `${s.ticker} (${((s.value / totalPortfolio) * 100).toFixed(1)}%)`
    }));

    const cashSlice = {
      id: stockSlices.length,
      value: CASH_AMOUNT,
      ticker: 'Cash',
      label: `Cash (${((CASH_AMOUNT / totalPortfolio) * 100).toFixed(1)}%)`,
      color: '#78909C'
    };

    return {
      slices: [...stockSlices, cashSlice],
      totalPortfolio,
      totalStockValue
    };
  }, [heaters]);

  return (
    <Container sx={{ display: 'flex', flexDirection: 'column' }}>
      <GlobalStyles
        styles={{
          '.print-only': {
            display: 'none !important'
          },
          '.print-watermark': {
            display: 'none !important'
          },
          '@media print': {
            body: {
              margin: 0,
              padding: 0
            },
            '.no-print': {
              display: 'none !important'
            },
            '.print-only': {
              display: 'block !important'
            },
            'nav': {
              display: 'none !important'
            },
            'aside': {
              display: 'none !important'
            },
            'header': {
              display: 'none !important'
            },
            '.MuiDrawer-root': {
              display: 'none !important'
            },
            '.MuiAppBar-root': {
              display: 'none !important'
            },
            '.MuiButton-root': {
              color: '#000 !important',
              backgroundColor: 'transparent !important',
              border: '1px solid #000 !important',
              textDecoration: 'underline !important'
            },
            '.MuiChip-root': {
              display: 'none !important'
            },
            '.MuiTooltip-tooltip': {
              display: 'none !important'
            },
            '.MuiTableContainer-root': {
              maxHeight: 'none !important',
              overflow: 'visible !important',
              boxShadow: 'none !important'
            },
            '.MuiPaper-root': {
              boxShadow: 'none !important',
              border: '1px solid #ddd'
            },
            '.MuiTableHead-root th': {
              position: 'static !important',
              backgroundColor: '#f5f5f5 !important',
              color: '#000 !important',
              border: '1px solid #ddd !important',
              padding: '8px !important'
            },
            '.MuiTableCell-root': {
              border: '1px solid #ddd !important',
              padding: '8px !important',
              fontSize: '11px !important'
            },
            '.MuiContainer-root': {
              maxWidth: '100% !important',
              padding: '0 !important'
            },
            '.MuiBox-root': {
              marginBottom: '20px !important',
              pageBreakInside: 'avoid'
            },
            'h5': {
              pageBreakAfter: 'avoid',
              marginTop: '15px',
              marginBottom: '8px',
              fontSize: '14px !important'
            },
            '.print-watermark': {
              display: 'block !important',
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%) rotate(-45deg)',
              fontSize: '65px',
              color: 'rgba(0, 0, 0, 0.03)',
              fontWeight: 'bold',
              zIndex: 999,
              whiteSpace: 'nowrap',
              pointerEvents: 'none'
            },
            '.MuiTable-root': {
              position: 'relative',
              zIndex: 1
            }
          }
        }}
      />
      <Box className="print-watermark">{user?.id}</Box>
      <Box className="print-only" sx={{ mb: 2, pb: 2, borderBottom: '2px solid #000', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ width: 120, mb: 1 }}>
            <CarnivoreLogo />
          </Box>
          <Typography variant='h4'>Sector Rotation</Typography>
          <Typography variant='caption'>{new Date().toLocaleDateString()} - User ID: {user?.id}</Typography>
        </Box>
      </Box>
      <Grid container justifyContent={'space-between'} className="no-print">
        <Grid item xs={12} md={6}>
          <Box display='flex' alignItems='center' gap={2}>
            <Typography variant='h4'>Sector Rotation</Typography>
            <KnowledgeBtn id='sector-heaters' />
          </Box>
        </Grid>
        <Grid item xs={12} md={6} textAlign={'right'} display={'flex'} alignItems={'center'} justifyContent={'flex-end'} gap={1}>
          <Tooltip title="Print">
            <IconButton onClick={handlePrint} size="medium" color="primary">
              <Print />
            </IconButton>
          </Tooltip>
          <Button variant='outlined' startIcon={<TrendingUpOutlined />} onClick={() => setPerformanceOpen(true)}>View Performance</Button>
        </Grid>
      </Grid>


      <StyleHelper.VerticalSpace />

      {loading && <LoadingTable rowCount={7} columnCount={5} />}

      {!loading &&
        heaters?.map((heater) => (
          <Box key={heater.id ?? ''} sx={{ mb: 4 }}>
            <Typography variant='h5'>{heater.title}</Typography>
            <StyleHelper.VerticalSpace />
            <TableContainer component={Paper} sx={{ maxHeight: '800px', overflow: 'auto' }}>
              <Table aria-label='heaters table'>
                <TableHead
                  sx={{
                    '& th': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      position: 'sticky',  // Make the headers sticky
                      top: 0,              // Set it to stick at the top
                      zIndex: 20
                    }
                  }}
                >
                  <TableRow>
                    <TableCell onClick={() => handleSort(heater.id ?? '', 'symbol')}>Symbol</TableCell>
                    <TableCell align='right' onClick={() => handleSort(heater.id ?? '', 'name')}>Name</TableCell>

                    <TableCell align='right' onClick={() => handleSort(heater.id ?? '', 'shares')}>Quantity</TableCell>
                    <TableCell align='right' onClick={() => handleSort(heater.id ?? '', 'entryPX')}>Entry PX</TableCell>
                    <TableCell align='right' onClick={() => handleSort(heater.id ?? '', 'currentPX')}>Current PX</TableCell>
                    <TableCell align='right' >Gain/Loss</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedStocks(heater.stocks, heater.id ?? '').map((stock) => (
                    <StyledTableRow key={stock.symbol}>
                      <TickerWrapper>
                        <TickerLink display='link' ticker={stock.symbol} />
                      </TickerWrapper>
                      <TableCell align='right'>{stock.name}</TableCell>
                      <TableCell align='right'>{stock.shares != null ? Formatters.formatCommas(stock.shares!) : "-"}</TableCell>
                      <TableCell align='right'>${Formatters.formatMoney(stock.entryPX)}</TableCell>
                      <TableCell align='right'>${Formatters.formatMoney(Number(stock.currentPX ?? 0))}</TableCell>
                      <TableCell
                        align='right'
                        sx={{
                          color: stock.entryPX < (stock.currentPX ?? 0) ? 'success.main' : 'error.main'
                        }}
                      >
                        {_calculatePercentageChange(stock.entryPX, stock.currentPX ?? 0)}
                      </TableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
      {!loading && heaters.length > 0 && (
        <>
          <Paper
            elevation={2}
            sx={{
              p: 3,
              mb: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
              color: 'primary.contrastText',
              borderRadius: 2
            }}
          >
            <AccountBalanceWallet sx={{ fontSize: 36, opacity: 0.9 }} />
            <Box>
              <Typography variant='body2' sx={{ opacity: 0.85, fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                Cash on Hand
              </Typography>
              <Typography variant='h4' sx={{ fontWeight: 700, letterSpacing: -0.5 }}>
                ${Formatters.formatMoney(CASH_AMOUNT)}
              </Typography>
            </Box>
            <Box sx={{ ml: 'auto', textAlign: 'right' }}>
              <Typography variant='body2' sx={{ opacity: 0.85, fontWeight: 500, letterSpacing: 0.5, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                Total Portfolio
              </Typography>
              <Typography variant='h5' sx={{ fontWeight: 600 }}>
                ${Formatters.formatMoney(portfolioData.totalPortfolio)}
              </Typography>
            </Box>
          </Paper>

          <Typography variant='h5' sx={{ mb: 2 }}>Portfolio Allocation</Typography>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: isMobile ? 'center' : 'flex-start',
                gap: 3
              }}
            >
              <Box sx={{ flexShrink: 0 }}>
                <PieChart
                  series={[
                    {
                      data: portfolioData.slices,
                      innerRadius: 40,
                      outerRadius: isMobile ? 120 : 150,
                      paddingAngle: 1,
                      cornerRadius: 3,
                      highlightScope: { fade: 'global', highlight: 'item' },
                      valueFormatter: (item) => {
                        const pct = ((item.value / portfolioData.totalPortfolio) * 100).toFixed(1)
                        return `$${Formatters.formatMoney(item.value)} (${pct}%)`
                      }
                    }
                  ]}
                  width={isMobile ? 320 : 700}
                  height={isMobile ? 500 : 420}
                  slotProps={{
                    legend: {
                      direction: 'column' as const,
                      position: {
                        vertical: isMobile ? 'bottom' as const : 'middle' as const,
                        horizontal: isMobile ? 'middle' as const : 'right' as const
                      },
                      itemMarkWidth: 10,
                      itemMarkHeight: 10,
                      markGap: 5,
                      itemGap: 4,
                      labelStyle: {
                        fontSize: 12,
                      }
                    }
                  }}
                />
              </Box>
            </Box>
          </Paper>
        </>
      )}

      <StyleHelper.DoubleVerticalSpace />
      {performanceOpen && <SectorPerformanceModal size={'lg'} open={performanceOpen} onClose={() => setPerformanceOpen(false)} />}
    </Container>
  );
}
